import { useStore } from 'react-redux';

import { useAppDispatch, useAppSelector } from 'features/app/hooks';
import type { RootState } from 'features/app/store/store';
import { AddHearingStatus } from 'features/call/call-details/enums';
import { setAddHearingStatus } from 'features/call/call-details/store';
import {
  findLatestHearingByPhoneNumber,
  toHearingDialString,
} from 'features/call/call-hearing/helpers';
import {
  disconnectAllHearings,
  setHearingLoadingStateByPhoneNumber,
  setCallDirection,
} from 'features/call/call-hearing/store';
import { ParticipantsStatusEventBus } from 'features/call/call-status/services';
import { callerSessionAnalyticsError } from 'features/caller-session/helpers';
import {
  checkShouldAllowNewHearingConnection,
  hearingDisconnection,
  voiceSessionEnded,
} from 'features/caller-session/store';
import { was911Dialed } from 'features/emergency/helpers';
import { useEmergencySpawnNewCall } from 'features/emergency/hooks';
import { isVrsModeSelector } from 'features/multi-mode/store';
import { isSpecialPhoneNumber } from 'features/n11/helpers';
import { handleError } from 'features/notification/store';
import {
  extractErrorMessage,
  isMeetingEndedError,
  clearInFlightAddParticipantRegistry,
  registerInFlightAddParticipant,
  takeInFlightAddParticipant,
} from 'features/voice-meeting/helpers';
import {
  useVoiceMeetingContext,
  useVoiceMeetingSession,
  useVoiceMeetingSessionInit,
} from 'features/voice-meeting/hooks';
import {
  resetVoiceSession,
  voiceSessionIdSelector,
} from 'features/voice-session/store';

export const useHearingConnect = () => {
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  const voiceSessionId = useAppSelector(voiceSessionIdSelector);
  const { service } = useVoiceMeetingContext();
  const { addParticipant, tearDownLocalMeeting, deleteVoiceSession } =
    useVoiceMeetingSession();
  const { spawnNewCallEmergency } = useEmergencySpawnNewCall();
  const { createNewVoiceSession, replaceN11Number, clearCallErrorIfExists } =
    useVoiceMeetingSessionInit();
  const isVrs = useAppSelector(isVrsModeSelector);

  const findHearingByPhone = (phoneNumber: string) =>
    findLatestHearingByPhoneNumber(
      store.getState().callHearing.hearings,
      phoneNumber
    );

  const cleanupEndedVoiceSession = async () => {
    if (service.meetingSession) {
      await tearDownLocalMeeting();
    }

    if (voiceSessionId) {
      await deleteVoiceSession({ emitCleanupIntent: false });
    }

    clearInFlightAddParticipantRegistry();
    service.resetService();
    dispatch(resetVoiceSession());
    dispatch(voiceSessionEnded());
    dispatch(disconnectAllHearings());
  };

  const connectHearingToExistingSession = async (phoneNumber: string) => {
    const requestedPhoneNumber = phoneNumber;
    const isSpecialVrsNumber = isVrs && isSpecialPhoneNumber(phoneNumber);
    try {
      const resolvedDialPhoneNumber = isSpecialVrsNumber
        ? ((await replaceN11Number(phoneNumber)) ?? requestedPhoneNumber)
        : requestedPhoneNumber;

      if (isSpecialVrsNumber) {
        if (requestedPhoneNumber === resolvedDialPhoneNumber) {
          dispatch(setAddHearingStatus(AddHearingStatus.ENTER_PHONE_NUMBER));
          return;
        }
      }
      clearCallErrorIfExists();
      const { shouldAllow, reasonMessage } = dispatch(
        checkShouldAllowNewHearingConnection(phoneNumber)
      );
      if (!shouldAllow) {
        dispatch(callerSessionAnalyticsError(reasonMessage));
        return;
      }
      // Register before $connecting so cancel cannot miss the promise.
      const addParticipantPromise = addParticipant(
        toHearingDialString(resolvedDialPhoneNumber)
      );
      registerInFlightAddParticipant(
        requestedPhoneNumber,
        addParticipantPromise,
        service.getSessionId?.() || voiceSessionId
      );
      ParticipantsStatusEventBus.hearing.$connecting.next(requestedPhoneNumber);
      const hearingParticipant = await addParticipantPromise;

      const liveHearing = findHearingByPhone(requestedPhoneNumber);
      if (liveHearing?.teardownIntent) {
        // Cancel won the race — bind id for cleanup/watcher but leave the
        // in-flight registry for cancel's take (may already be taken).
        console.log('HearingDebug: connect aborted (teardownIntent set)', {
          phoneNumber: requestedPhoneNumber,
          teardownIntent: liveHearing.teardownIntent,
          participantId: hearingParticipant?.id ?? null,
        });
        if (hearingParticipant) {
          ParticipantsStatusEventBus.hearing.$connectingId.next({
            phoneNumber: requestedPhoneNumber,
            id: hearingParticipant.id,
          });
        }
        return;
      }

      // Normal connect path — discard registry entry so it cannot leak.
      takeInFlightAddParticipant(requestedPhoneNumber);

      if (hearingParticipant) {
        ParticipantsStatusEventBus.hearing.$connectingId.next({
          phoneNumber: requestedPhoneNumber,
          id: hearingParticipant.id,
        });
      }
    } catch (error: unknown) {
      takeInFlightAddParticipant(requestedPhoneNumber);
      const isTerminalEndedSession = isMeetingEndedError(
        extractErrorMessage(error)
      );

      if (isTerminalEndedSession) {
        await cleanupEndedVoiceSession();
        dispatch(hearingDisconnection(requestedPhoneNumber));
        ParticipantsStatusEventBus.hearing.$connecting.next(
          requestedPhoneNumber
        );
        await createNewVoiceSession(requestedPhoneNumber);
        return;
      }

      dispatch(
        handleError({
          error,
          methodName: 'connectHearingToExistingSession',
          disableUserNotification: true,
        })
      );
      ParticipantsStatusEventBus.hearing.$disconnected.next({
        phoneNumber: requestedPhoneNumber,
      });
    }
  };

  const connectHearing = async (phoneNumber: string) => {
    dispatch(
      setHearingLoadingStateByPhoneNumber({ isLoading: true, phoneNumber })
    );

    // When reconnecting to a call, ensure it's marked as outbound
    if (!voiceSessionId) {
      dispatch(setCallDirection('outbound'));
    }

    if (was911Dialed(phoneNumber) && isVrs) {
      await spawnNewCallEmergency();
    } else if (!voiceSessionId) {
      await createNewVoiceSession(phoneNumber);
    } else {
      await connectHearingToExistingSession(phoneNumber);
    }

    // Keep cancel/disconnect loading while teardown is still pending.
    if (!findHearingByPhone(phoneNumber)?.teardownIntent) {
      dispatch(
        setHearingLoadingStateByPhoneNumber({ isLoading: false, phoneNumber })
      );
    }
  };

  return { connectHearing };
};
